import type { SeedkitCore } from '@seedkit/core';
import { useSeedkitService } from '@seedkit/core';
import { nextTick } from 'vue';
import {
  connectNotificationsHub,
  disconnectNotificationsHub,
  type ShowMessageFn,
} from './notifications-hub';
import { getSeedkitBaseUrlFromEnv } from './notifications-hub-env';

let apiBaseUrlOverride = '';
let showMessageRef: ShowMessageFn | null = null;

export function setNotificationsHubApiBaseUrl(url: string): void {
  apiBaseUrlOverride = String(url || '').replace(/\/$/, '');
}

export function registerNotificationsHubShowMessage(fn: ShowMessageFn): void {
  showMessageRef = fn;
}

function resolveHubBaseUrl(): string {
  return apiBaseUrlOverride || getSeedkitBaseUrlFromEnv() || '';
}

function buildHubUrl(): string | null {
  const base = resolveHubBaseUrl();
  if (!base) {
    return null;
  }
  return `${ base }/hub/notifications`;
}

/** Seedkit хранит JWT в `accessToken` (см. TokenStorage в vue-administration-module). */
function getHubAccessToken(): string | null {
  return (
    localStorage.getItem('accessToken')
    || localStorage.getItem('access-token')
    || null
  );
}

/**
 * useSeedkitService('notification') часто даёт null в первый кадр onMounted —
 * плагин ещё не зарегистрирован в Seedkit.services.
 */
async function resolveShowMessage(): Promise<ShowMessageFn | null> {
  if (showMessageRef) {
    return showMessageRef;
  }
  for (let i = 0; i < 20; i++) {
    const fn = useSeedkitService('notification');
    if (typeof fn === 'function') {
      showMessageRef = fn as ShowMessageFn;
      return showMessageRef;
    }
    await nextTick();
    await new Promise<void>((r) => void setTimeout(r, 25));
  }
  return null;
}

/** Один полёт: onMounted и login часто срабатывают подряд — не параллелим connect. */
let tryConnectInFlight: Promise<void> | null = null;

export function tryConnectNotificationsHub(): Promise<void> {
  if (tryConnectInFlight) {
    return tryConnectInFlight;
  }
  tryConnectInFlight = runTryConnectNotificationsHub().finally(() => {
    tryConnectInFlight = null;
  });
  return tryConnectInFlight;
}

async function runTryConnectNotificationsHub(): Promise<void> {
  const showMessage = await resolveShowMessage();
  if (!showMessage) {
    console.warn(
      '[notifications-hub] сервис notification недоступен — уведомления хаба отключены.',
    );
    return;
  }
  const token = getHubAccessToken();
  if (!token) {
    console.error('Нет токена доступа для подключения к уведомлениям');
    if (import.meta.env.DEV) {
      console.info(
        '[notifications-hub] нет токена — подключение только после входа (событие login).',
      );
    }
    return;
  }
  const hubUrl = buildHubUrl();
  if (!hubUrl) {
    console.error(
      'Уведомления: не задан base URL API (SEEDKIT_BASE_URL или http.baseUrl клиента).',
    );
    return;
  }
  try {
    await connectNotificationsHub({
      hubUrl,
      accessToken: token,
      showMessage,
    });
  } catch {
    /* сообщение уже показано в connect */
  }
}

export function bootstrapNotificationsHub(seedkit: SeedkitCore): void {
  seedkit.eventBus.on('login', () => {
    void tryConnectNotificationsHub();
  });
  seedkit.eventBus.on('logout', () => {
    void disconnectNotificationsHub();
  });
}
